import React from "react";
import toast, { Toaster } from "react-hot-toast";
import PropTypes from "prop-types";
import SuggestionInput from "shared/@forms/SuggestionInput";
import Loader from "shared/Loader";
import { ButtonPurpose } from "shared/Button/Button";
import AlertNotice from "shared/AlertNotice";
import Pagination from "shared/Pagination";
import { orders as ordersApi, ibos as ibosApi, errorMessage } from "shared/api";
import { isRealApiEnabled } from "shared/api/client";
import SearchTips from "../../../Statistics/components/SearchTips";
import OrderHistoryList from "../../OrderHistoryList";
import { OrderDetailsCard } from "../OrderDetailsCard/OrderDetailsCard";
import OrderCard from "../OrderCard";
import IboHeader from "../IboHeader";
import NotesPanel from "../NotesPanel";
import ReasonModal from "../ReasonModal";
import PreviouslyViewed from "shared/PreviouslyViewed";
import {
  getPreviouslyViewed,
  recordView,
} from "shared/@utils/previouslyViewed";
import { ChevronLeft } from "react-ikonate";
import { orderHistoryTips } from "../../searchTips";
import { useIboSearch } from "../../hooks/useIboSearch";
import {
  usePersistedPersonId,
  getIboPeople,
} from "../../hooks/usePersistedPersonId";
import styles from "./OrderHistory.module.css";

// Read the payment-execution result the backend folds into the newest audit
// entry (execution = { provider, status, providerRef }). Lets the UI tell the
// truth about whether money actually moved instead of always claiming success.
const latestExecution = (serverOrder, kind) => {
  const trail = Array.isArray(serverOrder?.auditTrail)
    ? serverOrder.auditTrail
    : [];
  for (let i = trail.length - 1; i >= 0; i -= 1) {
    const entry = trail[i];
    if (entry && entry.execution && (!kind || entry.kind === kind)) {
      return entry.execution;
    }
  }
  return null;
};

// Honest toast for a money-moving remediation. `succeeded` (real or simulated)
// is the only case that gets a green success; `recorded` (provider off, no
// money moved) and a missing result get a neutral, truthful note.
const notifyRemediation = (execution, { succeeded, recorded, pending }) => {
  const sim = execution?.provider === "simulate" ? " (simulated)" : "";
  const status = execution?.status;
  if (status === "succeeded") {
    toast.success(`${succeeded}${sim}`);
  } else if (status === "pending") {
    toast.success(`${pending}${sim}`);
  } else if (status === "failed") {
    toast.error(
      execution?.error
        ? `${succeeded} failed: ${execution.error}`
        : `${succeeded} failed`
    );
  } else {
    // "recorded" or no execution info — nothing was actually executed.
    toast(recorded, { icon: "ℹ️" });
  }
};

const applyLocalSearch = (orders, searchTerm) => {
  const trimmed = (searchTerm || "").trim().toLowerCase();
  if (!trimmed) {
    return orders;
  }
  return orders.filter((order) => {
    // Match each field independently so mock-mode search behaves identically to
    // the server (seed + Mongo both match orderNum/status/trackingNum on their
    // own, not a concatenated string that could match across field boundaries).
    return (
      String(order.orderNum || "")
        .toLowerCase()
        .includes(trimmed) ||
      String(order.status || "")
        .toLowerCase()
        .includes(trimmed) ||
      String(order.trackingNum || "")
        .toLowerCase()
        .includes(trimmed)
    );
  });
};

const isPendingStatus = (status) =>
  String(status || "").toLowerCase() === "pending";

const isCartOrder = (order) =>
  String(order?.status || "").toLowerCase() === "cart";

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

// Build the props for <OrderDetailsCard>. Orders carry a rich `details`
// sub-object once fulfillment has run, but freshly-created admin orders only
// have top-level fields (status/total/itemsDetail), so `details` is null and
// the panel would render entirely blank. When that's the case, derive a
// details view from what we do know so the operator still sees the status and
// totals; genuinely-unknown fields (tracking, trace ids, tax) stay empty and
// the card shows a placeholder for them.
const buildOrderDetails = (order, ibo) => {
  const details = order?.details || {};
  const hasDetails = Object.values(details).some(
    (v) => v !== "" && v !== null && v !== undefined
  );
  // The carrier link rides on the order itself, not the details snapshot.
  if (hasDetails) return { ...details, trackingUrl: order?.trackingUrl || "" };
  return {
    orderId: order?.orderNum || order?.id || "",
    lastName: ibo?.name || "",
    address: ibo?.address || "",
    status: order?.status || "",
    trackingNum: order?.trackingNum || "",
    trackingUrl: order?.trackingUrl || "",
    subTotal: money(order?.total),
    total: money(order?.total),
  };
};

// Stable per-row key. Order line items in the mock data have no unique id and
// can share a SKU, so fall back to the array index for a stable, unique key.
const itemKeyOf = (item, index) => item.id ?? index;

// Reconstruct the per-order remediation UI state (audit log, refunded qty per
// line, cancelled line ids, whole-order-cancelled flag) from an order's
// persisted `auditTrail`. This lets refunds/cancellations survive a reload:
// the backend is the source of truth and the UI re-derives from it on load.
const deriveFromAuditTrail = (order) => {
  const trail = Array.isArray(order?.auditTrail) ? order.auditTrail : [];
  // Map a persisted (stringified) itemId back to this order's local row key so
  // Set/lookup comparisons match itemKeyOf (which yields a numeric index when
  // line items have no stable id).
  const localKeys = (order?.itemsDetail || []).map((it, i) => itemKeyOf(it, i));
  const toLocalKey = (raw) => {
    const match = localKeys.find((k) => String(k) === String(raw));
    return match !== undefined ? match : raw;
  };

  const refundedMap = {};
  const cancelledSet = new Set();
  trail.forEach((entry) => {
    (entry.items || []).forEach((line) => {
      const key = toLocalKey(line.itemId);
      if (entry.kind === "refund") {
        refundedMap[key] =
          (refundedMap[key] || 0) + (Number(line.quantity) || 0);
      } else if (entry.kind === "cancel") {
        cancelledSet.add(key);
      }
    });
  });
  const cancelledOrder =
    localKeys.length > 0 && localKeys.every((k) => cancelledSet.has(k));
  return { audit: trail, refundedMap, cancelledSet, cancelledOrder };
};

// Map order line items into the shape OrderCard renders. Refunds and cancels
// are now driven by row checkboxes + a bulk toolbar (see OrderCard), so the
// only per-row button left is "Add to Cart" on delivered orders. Each row also
// carries its refunded / cancelled state so OrderCard can badge it and disable
// re-selecting anything that's already fully resolved.
const buildActionItems = (
  items,
  orderStatus,
  handlers,
  cancelledIds,
  refundedMap
) => {
  const pending = isPendingStatus(orderStatus);
  const cancelledSet = cancelledIds || new Set();
  const refunds = refundedMap || {};
  return (items || []).map((item, index) => {
    const itemId = itemKeyOf(item, index);
    const base = { ...item, id: itemId };

    if (cancelledSet.has(itemId)) {
      return {
        ...base,
        cancelled: true,
        showButtons: false,
        selectable: false,
      };
    }

    if (pending) {
      // Pending items can be cancelled in bulk; no per-row button.
      return {
        ...base,
        showButtons: false,
        selectable: true,
      };
    }

    // Delivered / refundable items.
    const totalQty = Number(item.qty) || 1;
    const refundedQty = refunds[itemId] || 0;
    const fullyRefunded = refundedQty >= totalQty;
    return {
      ...base,
      refunded: refundedQty > 0,
      refundedQty,
      fullyRefunded,
      // A fully refunded row can't be selected/refunded again.
      selectable: !fullyRefunded,
      showButtons: true,
      secondaryBtnText: "Re-Ship",
      primaryBtnText: null,
      onSecondaryClick: () => handlers.onReship({ ...base, _itemId: itemId }),
    };
  });
};

const formatActionDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};

export function OrderHistory({
  selectedIbo,
  onSelectIbo,
  onReship,
  iboQuery,
  onIboQueryChange,
  refreshToken = 0,
}) {
  const [orderQuery, setOrderQuery] = React.useState("");
  const [orderSuggestions, setOrderSuggestions] = React.useState([]);
  const [isOrderLoading, setIsOrderLoading] = React.useState(false);
  const [activeOrder, setActiveOrder] = React.useState(null);
  const [activeOrderLoading, setActiveOrderLoading] = React.useState(false);
  const [activeOrderError, setActiveOrderError] = React.useState(null);
  const activeOrderReqRef = React.useRef(0);

  // Which person on a shared (two-person) IBO account the rep is viewing.
  // Persisted per IBO so it survives tab/page changes and refreshes.
  const people = React.useMemo(() => getIboPeople(selectedIbo), [selectedIbo]);
  const [selectedPersonId, setSelectedPersonId] =
    usePersistedPersonId(selectedIbo);

  const handleBackToOrders = React.useCallback(() => {
    // Invalidate any in-flight order-detail fetch so a slow response can't
    // resurrect the detail view after the user has navigated away.
    activeOrderReqRef.current += 1;
    setActiveOrderLoading(false);
    setActiveOrderError(null);
    setActiveOrder(null);
    setOrderQuery("");
    setOrderSuggestions([]);
  }, []);

  const handleIboQueryReset = React.useCallback(() => {
    activeOrderReqRef.current += 1;
    setActiveOrderLoading(false);
    setActiveOrderError(null);
    onSelectIbo(null);
    setOrderQuery("");
    setOrderSuggestions([]);
    setActiveOrder(null);
  }, [onSelectIbo]);

  const handleClearIbo = React.useCallback(() => {
    handleIboQueryReset();
    onIboQueryChange("");
  }, [handleIboQueryReset, onIboQueryChange]);

  const handleIboSelect = React.useCallback(
    (ibo) => {
      activeOrderReqRef.current += 1;
      setActiveOrderLoading(false);
      setActiveOrderError(null);
      onSelectIbo(ibo);
      setOrderQuery("");
      setOrderSuggestions([]);
      setActiveOrder(null);
    },
    [onSelectIbo]
  );

  const {
    query,
    suggestions,
    isLoading,
    totalCount: iboTotalCount,
    hasMore: hasMoreIbos,
    isLoadingMore: isLoadingMoreIbos,
    loadMore: loadMoreIbos,
    fetchSuggestions,
    handleQueryChange,
    handleSelectIbo,
  } = useIboSearch({
    searchFn: ibosApi.search,
    onSelect: handleIboSelect,
    onQueryReset: handleIboQueryReset,
    query: iboQuery,
    setQuery: onIboQueryChange,
  });

  const handleOrderSearchChange = React.useCallback((event) => {
    setOrderQuery(event.target.value);
  }, []);

  const fetchOrderSuggestions = React.useCallback(
    (value) => {
      if (!selectedIbo) {
        setOrderSuggestions([]);
        setIsOrderLoading(false);
        return;
      }

      const normalized = value.trim().toLowerCase();
      if (!normalized) {
        setOrderSuggestions([]);
        setIsOrderLoading(false);
        return;
      }

      setIsOrderLoading(true);

      const matches = (selectedIbo.orders || []).filter(({ orderNum = "" }) =>
        orderNum.toLowerCase().includes(normalized)
      );

      setOrderSuggestions(matches);
      setIsOrderLoading(false);
    },
    [selectedIbo]
  );

  const renderSuggestion = React.useCallback(
    (ibo, closePanel) => (
      <button
        key={ibo.iboNumber}
        type="button"
        title={`View order history for ${ibo.name} (IBO: ${ibo.iboNumber}).`}
        className={styles.suggestionRow}
        onClick={() => {
          recordView("ibo_search", {
            id: ibo.iboNumber || "",
            name: ibo.name || "",
            sku: "",
          });
          handleSelectIbo(ibo);
          closePanel();
        }}
      >
        <span className={styles.suggestionLabel}>IBO:</span>
        <span className={styles.suggestionPill}>
          {ibo.searchLabel || ibo.name} (IBO: {ibo.iboNumber})
        </span>
      </button>
    ),
    [handleSelectIbo]
  );

  const renderOrderSuggestion = React.useCallback((order, closePanel) => {
    if (!order?.orderNum) {
      return null;
    }

    return (
      <button
        key={order.orderNum}
        type="button"
        title={`Open order #${order.orderNum}.`}
        className={styles.suggestionRow}
        onClick={() => {
          setOrderQuery(order.orderNum);
          setActiveOrder(order);
          closePanel();
        }}
      >
        <span className={styles.suggestionLabel}>Order #:</span>
        <span className={styles.suggestionPill}>{order.orderNum}</span>
      </button>
    );
  }, []);

  // Remediation modal state: kind = "cancel" | "refund", items = selected rows.
  const [remediation, setRemediation] = React.useState(null);
  const [remediationBusy, setRemediationBusy] = React.useState(false);
  // Per-order audit trail keyed by order id (mock-local until backend exists).
  const [auditLog, setAuditLog] = React.useState({});
  // Per-order set of cancelled item ids (mock-local).
  const [cancelledItems, setCancelledItems] = React.useState({});
  // Per-order map of refunded quantity per item id (mock-local). Lets us badge
  // partially/fully refunded rows and block refunding the same units twice.
  const [refundedItems, setRefundedItems] = React.useState({});
  // Per-order full-cancel flag (mock-local).
  const [cancelledOrders, setCancelledOrders] = React.useState({});

  const handleOrderView = React.useCallback((order) => {
    if (order?.orderNum) {
      setOrderQuery(order.orderNum);
    }
    // Optimistically render what we already have from the list so the user
    // sees the page immediately; refresh from the server in the background.
    setActiveOrder(order);
    if (!order?.id && !order?.orderNum) return;
    const reqId = ++activeOrderReqRef.current;
    setActiveOrderLoading(true);
    setActiveOrderError(null);
    ordersApi
      .get(order.id || order.orderNum)
      .then((full) => {
        if (reqId !== activeOrderReqRef.current) return;
        // Real API returns the full order detail (details / itemsDetail /
        // auditTrail), so refresh from it. In mock mode the response is a slim
        // generic order, so keep the richer list payload we optimistically set.
        if (isRealApiEnabled() && full && Array.isArray(full.itemsDetail)) {
          setActiveOrder(full);
        }
        setActiveOrderLoading(false);
      })
      .catch((err) => {
        if (reqId !== activeOrderReqRef.current) return;
        setActiveOrderError(
          errorMessage(err, "Couldn't load this order's details.")
        );
        setActiveOrderLoading(false);
      });
  }, []);

  const [orders, setOrders] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [ordersError, setOrdersError] = React.useState(null);
  // Page is 0-indexed to match PagingControls (which renders page+1).
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(8);
  const [totalCount, setTotalCount] = React.useState(0);
  const reqIdRef = React.useRef(0);

  React.useEffect(() => {
    setPage(0);
  }, [selectedIbo, orderQuery, pageSize, selectedPersonId]);

  React.useEffect(() => {
    if (!selectedIbo) {
      setOrders([]);
      setTotalCount(0);
      return;
    }
    const reqId = ++reqIdRef.current;
    setOrdersLoading(true);
    setOrdersError(null);

    ordersApi
      .list({
        iboId: selectedIbo.id,
        // Two-person accounts: only the selected person's orders (plus
        // account-level orders with no userId stamp).
        userId: selectedPersonId || undefined,
        search: orderQuery,
        page: page + 1,
        pageSize,
      })
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        if (isRealApiEnabled()) {
          const items = Array.isArray(res) ? res : res?.items || [];
          setOrders(items);
          setTotalCount(res?.total || items.length);
        } else {
          const all = (selectedIbo.orders || []).filter(
            (order) => !isCartOrder(order)
          );
          const filtered = applyLocalSearch(all, orderQuery);
          const total = filtered.length;
          const start = page * pageSize;
          setOrders(filtered.slice(start, start + pageSize));
          setTotalCount(total);
        }
        setOrdersLoading(false);
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setOrdersError(errorMessage(err, "Couldn't load the order history."));
        setOrdersLoading(false);
      });
  }, [selectedIbo, orderQuery, page, pageSize, refreshToken, selectedPersonId]);

  // 0-indexed last page (for PagingControls which adds 1 internally).
  const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);

  const orderKey = activeOrder?.id ?? activeOrder?.orderNum;

  const appendAudit = React.useCallback((key, entry) => {
    setAuditLog((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), entry],
    }));
  }, []);

  // Overwrite the per-order remediation state from a server order that carries a
  // persisted auditTrail (authoritative). Used both after an action returns the
  // updated order and when an order with existing history is loaded.
  const applyRemediationState = React.useCallback((key, order) => {
    if (!key) return;
    const derived = deriveFromAuditTrail(order);
    setAuditLog((prev) => ({ ...prev, [key]: derived.audit }));
    setRefundedItems((prev) => ({ ...prev, [key]: derived.refundedMap }));
    setCancelledItems((prev) => ({ ...prev, [key]: derived.cancelledSet }));
    setCancelledOrders((prev) => ({ ...prev, [key]: derived.cancelledOrder }));
  }, []);

  // On loading an order that already has persisted remediation history, hydrate
  // the UI from it so refunds/cancellations survive a page reload. Mock-mode
  // orders have no auditTrail, so this no-ops and the local optimistic state
  // (updated in handleRemediationSubmit) remains the source of truth.
  React.useEffect(() => {
    if (!orderKey || !activeOrder) return;
    if (
      Array.isArray(activeOrder.auditTrail) &&
      activeOrder.auditTrail.length > 0
    ) {
      applyRemediationState(orderKey, activeOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey, activeOrder?.auditTrail?.length]);

  // Map a set of selected row ids (from OrderCard) back to the order's line
  // items, attaching the already-refunded quantity so the modal can cap the
  // refundable amount per line.
  const collectSelectedItems = React.useCallback(
    (selectedIds) => {
      const ids = new Set(selectedIds);
      const refunds = (orderKey && refundedItems[orderKey]) || {};
      return (activeOrder?.itemsDetail || [])
        .map((it, i) => ({ ...it, id: itemKeyOf(it, i) }))
        .filter((it) => ids.has(it.id))
        .map((it) => ({ ...it, refundedQty: refunds[it.id] || 0 }));
    },
    [activeOrder, orderKey, refundedItems]
  );

  const handleBulkRefund = React.useCallback(
    (selectedIds) => {
      const items = collectSelectedItems(selectedIds);
      if (items.length === 0) return;
      setRemediation({ kind: "refund", items });
    },
    [collectSelectedItems]
  );

  const handleBulkCancel = React.useCallback(
    (selectedIds) => {
      const items = collectSelectedItems(selectedIds);
      if (items.length === 0) return;
      setRemediation({ kind: "cancel", items });
    },
    [collectSelectedItems]
  );
  // Re-Ship: funnel the chosen order line(s) into the admin "New Order" cart
  // (Charge Card On File stays OFF so the customer is never re-billed) and jump
  // to Create Order → New Order, where the rep reviews and places a replacement
  // order. This creates a brand-new order and never touches the original.
  const reshipItems = React.useCallback(
    (items) => {
      if (!items || items.length === 0) return;
      onReship(items, activeOrder?.id || activeOrder?.orderNum);
      toast.success(
        `Re-Shipping ${items.length} item${
          items.length === 1 ? "" : "s"
        } — review the new order under Create Order.`
      );
    },
    [onReship, activeOrder]
  );

  const handleReshipItem = React.useCallback(
    (item) => reshipItems([item]),
    [reshipItems]
  );

  const handleBulkReship = React.useCallback(
    (selectedIds) => {
      const items = collectSelectedItems(selectedIds);
      reshipItems(items);
    },
    [collectSelectedItems, reshipItems]
  );

  const handleCloseModal = React.useCallback(() => {
    if (!remediationBusy) setRemediation(null);
  }, [remediationBusy]);

  const handleRemediationSubmit = React.useCallback(
    async ({ reason, lines, totalAmount, shippingCents }) => {
      if (!remediation || !activeOrder) return;
      const { kind, items } = remediation;
      const orderId = activeOrder.id || activeOrder.orderNum;
      setRemediationBusy(true);
      try {
        let label;
        let serverOrder = null;
        if (kind === "refund") {
          const payloadItems = lines.map((l) => ({
            itemId: l.id,
            quantity: l.quantity,
            amountCents: Math.round((l.amount || 0) * 100),
          }));
          const res = await ordersApi.refund(orderId, {
            items: payloadItems,
            reason,
            ...(shippingCents > 0 ? { shippingCents } : {}),
          });
          if (res && Array.isArray(res.auditTrail)) serverOrder = res;
          // Optimistically accumulate refunded quantity per line so partially
          // refunded rows keep their badge and can't be over-refunded.
          setRefundedItems((prev) => {
            const current = { ...(prev[orderKey] || {}) };
            lines.forEach((l) => {
              current[l.id] = (current[l.id] || 0) + l.quantity;
            });
            return { ...prev, [orderKey]: current };
          });
          const count = lines.reduce((sum, l) => sum + l.quantity, 0);
          label = `Refunded ${money(totalAmount)} (${count} item${
            count === 1 ? "" : "s"
          }${shippingCents > 0 ? " + shipping" : ""})`;
          notifyRemediation(latestExecution(serverOrder, "refund"), {
            succeeded: "Refund successful",
            pending: "Refund submitted — pending confirmation",
            recorded:
              "Refund recorded — no money moved yet (payment execution is off)",
          });
        } else if (kind === "cancel") {
          const payloadItems = items.map((it) => ({ itemId: it.id }));
          const res = await ordersApi.cancelItem(orderId, {
            items: payloadItems,
            reason,
          });
          if (res && Array.isArray(res.auditTrail)) serverOrder = res;
          const cancelledNow = new Set([
            ...(cancelledItems[orderKey] || []),
            ...items.map((it) => it.id),
          ]);
          setCancelledItems((prev) => ({ ...prev, [orderKey]: cancelledNow }));
          // Flag the whole order once every line is cancelled.
          const allIds = (activeOrder.itemsDetail || []).map((it, i) =>
            itemKeyOf(it, i)
          );
          if (allIds.every((id) => cancelledNow.has(id))) {
            setCancelledOrders((prev) => ({ ...prev, [orderKey]: true }));
          }
          label = `Cancelled ${items.length} item${
            items.length === 1 ? "" : "s"
          }`;
          {
            const noun = items.length === 1 ? "item" : "items";
            notifyRemediation(latestExecution(serverOrder, "cancel"), {
              succeeded: `Cancelled ${items.length} ${noun}`,
              pending: "Cancellation submitted — pending",
              recorded: `Cancelled ${items.length} ${noun} — recorded only, no refund issued (payment execution is off)`,
            });
          }
        }
        if (serverOrder) {
          // Real API returned the updated order — reconcile to the persisted
          // audit trail (authoritative, survives reload) and stamp the loaded
          // order so re-selecting it stays consistent.
          applyRemediationState(orderKey, serverOrder);
          setActiveOrder((prev) =>
            prev && (prev.id === orderId || prev.orderNum === orderId)
              ? { ...prev, auditTrail: serverOrder.auditTrail }
              : prev
          );
        } else {
          appendAudit(orderKey, {
            id: `audit_${Date.now()}`,
            kind,
            label,
            reason,
            actor: "support.user@wwg.local",
            occurredAt: new Date().toISOString(),
          });
        }
        setRemediation(null);
      } catch (err) {
        toast.error(errorMessage(err, "The action couldn't be completed."));
      } finally {
        setRemediationBusy(false);
      }
    },
    [
      remediation,
      activeOrder,
      orderKey,
      appendAudit,
      applyRemediationState,
      cancelledItems,
    ]
  );

  const activeOrderAudit = React.useMemo(
    () => (orderKey ? auditLog[orderKey] || [] : []),
    [orderKey, auditLog]
  );

  // Shipping is refunded with the last items on the order (see ReasonModal).
  // The modal needs to know what shipping cost, whether it's already been
  // refunded, and how many units outside this refund are still refundable.
  const refundShipping = React.useMemo(() => {
    if (!activeOrder) return null;
    const amount = Number(activeOrder.shippingTotal) || 0;
    const tax = Number(activeOrder.shippingTax) || 0;
    if (amount + tax <= 0) return null;
    const refunded = activeOrderAudit.some(
      (e) => e && e.kind === "refund" && Number(e.shippingCents) > 0
    );
    return { amount, tax, refunded };
  }, [activeOrder, activeOrderAudit]);
  const otherRemaining = React.useMemo(() => {
    if (!activeOrder || remediation?.kind !== "refund") return 0;
    const selected = new Set((remediation.items || []).map((it) => it.id));
    const cancelled = (orderKey && cancelledItems[orderKey]) || new Set();
    const refunds = (orderKey && refundedItems[orderKey]) || {};
    return (activeOrder.itemsDetail || []).reduce((sum, it, i) => {
      const key = itemKeyOf(it, i);
      if (selected.has(key) || cancelled.has(key)) return sum;
      const qty = Number(it.qty) || 1;
      return sum + Math.max(0, qty - (refunds[key] || 0));
    }, 0);
  }, [activeOrder, remediation, orderKey, cancelledItems, refundedItems]);
  const activeOrderCancelledIds = orderKey
    ? cancelledItems[orderKey] || new Set()
    : new Set();
  const activeOrderRefundedMap = orderKey ? refundedItems[orderKey] || {} : {};
  const isOrderFullyCancelled = orderKey ? !!cancelledOrders[orderKey] : false;
  const activeOrderStatus = activeOrder?.details?.status || activeOrder?.status;
  const isPendingOrder = isPendingStatus(activeOrderStatus);

  return (
    <div className={styles.orderHistory}>
      {selectedIbo && activeOrder ? (
        <div className={styles.detailView}>
          <button
            type="button"
            className={styles.backButton}
            title="Go back to the list of orders."
            onClick={handleBackToOrders}
          >
            <ChevronLeft size={18} />
            <span className={styles.backLabel}>Orders</span>
          </button>
          <section className={styles.detailHeader}>
            <p className={styles.iboNumber}>IBO {selectedIbo.iboNumber}</p>
            <h1 className={styles.iboName}>
              {selectedIbo.displayName || selectedIbo.name}
            </h1>
            <p className={styles.orderNumber}>Order # {activeOrder.orderNum}</p>
          </section>

          <div className={styles.detailCards}>
            {activeOrderError && (
              <div className={styles.alertWrap}>
                <AlertNotice title="Couldn't refresh order">
                  {activeOrderError}
                </AlertNotice>
              </div>
            )}
            {activeOrderLoading && (
              <div className={styles.loaderBanner}>Refreshing order…</div>
            )}
            <OrderDetailsCard
              {...buildOrderDetails(activeOrder, selectedIbo)}
            />

            <div className={styles.rightColumn}>
              <OrderCard
                items={buildActionItems(
                  activeOrder.itemsDetail,
                  activeOrderStatus,
                  { onReship: handleReshipItem },
                  activeOrderCancelledIds,
                  activeOrderRefundedMap
                )}
                selectable={!isOrderFullyCancelled}
                bulkActions={
                  isOrderFullyCancelled
                    ? null
                    : isPendingOrder
                    ? [
                        {
                          key: "cancel",
                          label: "Cancel Selected",
                          purpose: ButtonPurpose.delete,
                          onClick: handleBulkCancel,
                        },
                      ]
                    : [
                        {
                          key: "reship",
                          label: "Re-Ship Selected",
                          purpose: ButtonPurpose.generic,
                          onClick: handleBulkReship,
                        },
                        {
                          key: "refund",
                          label: "Refund Selected",
                          purpose: ButtonPurpose.delete,
                          onClick: handleBulkRefund,
                        },
                      ]
                }
                cancelledBanner={
                  isOrderFullyCancelled
                    ? "This order has been cancelled."
                    : null
                }
              />

              {activeOrderAudit.length > 0 && (
                <section className={styles.auditCard}>
                  <h3 className={styles.auditHeading}>Audit Trail</h3>
                  <ul className={styles.auditList}>
                    {activeOrderAudit.map((entry) => (
                      <li key={entry.id} className={styles.auditEntry}>
                        <div className={styles.auditEntryHead}>
                          <span className={styles.auditEntryLabel}>
                            {entry.label}
                          </span>
                          <span className={styles.auditEntryTime}>
                            {formatActionDate(entry.occurredAt)}
                          </span>
                        </div>
                        <div className={styles.auditEntryMeta}>
                          By {entry.actor}
                        </div>
                        <div className={styles.auditEntryReason}>
                          “{entry.reason}”
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* The IBO search stays pinned at the top in every non-detail state
              (matches Figma + Create Order): before a selection it's the lookup
              box; after one it shows the active IBO and lets the rep switch
              IBOs without a separate back button. */}
          <div className={styles.toolbar}>
            <div className={styles.searchLabel}>
              Search for IBOs by Name or IBO Number
            </div>
            <div className={styles.searchInput}>
              <SuggestionInput
                type="search"
                placeholder="Search IBOs"
                aria-label="Search IBOs"
                value={query}
                onChange={handleQueryChange}
                fetchSuggestions={fetchSuggestions}
                suggestions={suggestions}
                isLoading={isLoading}
                totalCount={iboTotalCount}
                hasMore={hasMoreIbos}
                isLoadingMore={isLoadingMoreIbos}
                onLoadMore={loadMoreIbos}
                minLength={1}
              >
                {renderSuggestion}
              </SuggestionInput>
            </div>
          </div>

          {selectedIbo ? (
            <>
              <IboHeader
                selectedIbo={selectedIbo}
                people={people}
                selectedPersonId={selectedPersonId}
                onPersonChange={setSelectedPersonId}
                onClear={handleClearIbo}
              />

              <div className={styles.orderSearchRow}>
                <div className={`${styles.orderSearch} ${styles.searchInput}`}>
                  <SuggestionInput
                    type="search"
                    placeholder="Search Orders"
                    aria-label="Search Orders"
                    value={orderQuery}
                    onChange={handleOrderSearchChange}
                    fetchSuggestions={fetchOrderSuggestions}
                    suggestions={orderSuggestions}
                    isLoading={isOrderLoading}
                    minLength={1}
                  >
                    {renderOrderSuggestion}
                  </SuggestionInput>
                </div>
              </div>

              <div className={styles.ordersColumn}>
                {ordersError && (
                  <div className={styles.alertWrap}>
                    <AlertNotice title="Couldn't load orders">
                      {ordersError}
                    </AlertNotice>
                  </div>
                )}

                {ordersLoading ? (
                  <div className={styles.loaderWrap}>
                    <Loader size={60} />
                  </div>
                ) : (
                  <>
                    <OrderHistoryList
                      orders={orders}
                      onOrderView={handleOrderView}
                    />
                    {totalCount > 0 && (
                      <div className={styles.pagingWrap}>
                        <Pagination
                          page={page + 1}
                          totalPages={Math.max(1, lastPage + 1)}
                          onPageChange={(next) => setPage(next - 1)}
                          pageSize={pageSize}
                          onPageSizeChange={setPageSize}
                          pageSizeOptions={[8, 16, 32, 64, 128]}
                          ariaLabel="Order history pages"
                          resultsLabel={`${totalCount} order${
                            totalCount === 1 ? "" : "s"
                          }`}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className={styles.notesWrap}>
                <NotesPanel iboId={selectedIbo.iboNumber} />
              </div>
            </>
          ) : (
            <>
              <section className={styles.emptyState}>
                <h2 className={styles.emptyTitle}>No IBO selected</h2>
                <p className={styles.emptySubtitle}>
                  But you can use the search box above to find one.
                </p>
              </section>

              <PreviouslyViewed
                items={getPreviouslyViewed("ibo_search")}
                onSelect={(item) => {
                  fetchSuggestions(item.name);
                }}
              />

              <SearchTips tips={orderHistoryTips} />
            </>
          )}
        </>
      )}

      <ReasonModal
        open={!!remediation}
        kind={remediation?.kind}
        items={remediation?.items || []}
        busy={remediationBusy}
        shipping={refundShipping}
        otherRemaining={otherRemaining}
        onCancel={handleCloseModal}
        onSubmit={handleRemediationSubmit}
        title={
          remediation?.kind === "refund"
            ? // Refund mode derives its own heading from the live unit count
              // inside ReasonModal; this is only a non-empty fallback.
              "Refund items"
            : `Cancel ${remediation?.items?.length || 0} item${
                (remediation?.items?.length || 0) === 1 ? "" : "s"
              }`
        }
        description={
          remediation?.kind === "refund"
            ? "Choose how many of each item to refund. The total is calculated automatically — no card is charged. The reason is saved to the audit trail."
            : "Cancellation cannot be undone. The reason is saved to the audit trail."
        }
        submitLabel={remediation?.kind === "refund" ? "Refund" : "Cancel items"}
      />

      <Toaster position="bottom-right" />
    </div>
  );
}

OrderHistory.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedIbo: PropTypes.object,
  onSelectIbo: PropTypes.func.isRequired,
  onReship: PropTypes.func,
  iboQuery: PropTypes.string,
  onIboQueryChange: PropTypes.func,
  refreshToken: PropTypes.number,
};

OrderHistory.defaultProps = {
  selectedIbo: null,
  onReship: () => {},
  iboQuery: undefined,
  onIboQueryChange: undefined,
};

export default OrderHistory;
